/**
 * PRP-011 — LLM-assisted rule authoring modal.
 *
 * Three steps:
 *   1. Describe — large textarea, admin types the rule in plain English.
 *   2. Review  — shows the LLM-generated rule:
 *        - plain-English render (large, readable)
 *        - confidence indicator (green/yellow/red)
 *        - ambiguities + warnings (bullet lists)
 *        - collapsed JSON (expandable)
 *      Actions: Save / Edit description / Switch to manual editor.
 *   3. Saved  — confirmation toast.
 *
 * The modal entry point is exposed from the parent (RulesPage) via two
 * buttons:
 *   - "Describe in plain language" (recommended) → opens this modal at step 1.
 *   - "Use the structured editor" (power users) → falls back to legacy editor
 *     (not yet implemented in PRP-002; we render a TODO note).
 */
import * as React from 'react';
import { X, Sparkles, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react';

import { RulesAPI } from '../../lib/rulesApi';
import { cn, getErrorMessage } from '../../lib/utils';
import type { TranslateRuleResponse } from '../../types/rules';

interface RuleAuthoringModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: (_ruleId: string, _ruleName: string) => void;
}

type Step = 'describe' | 'review' | 'saved';

const RuleAuthoringModal: React.FC<RuleAuthoringModalProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const [step, setStep] = React.useState<Step>('describe');
  const [description, setDescription] = React.useState('');
  const [translating, setTranslating] = React.useState(false);
  const [translation, setTranslation] = React.useState<TranslateRuleResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [errorDetail, setErrorDetail] = React.useState<unknown>(null);
  const [saving, setSaving] = React.useState(false);
  const [showJson, setShowJson] = React.useState(false);
  const [savedRule, setSavedRule] = React.useState<{ id: string; name: string } | null>(null);

  // Reset modal state when it closes.
  React.useEffect(() => {
    if (!open) {
      setStep('describe');
      setDescription('');
      setTranslation(null);
      setError(null);
      setErrorDetail(null);
      setShowJson(false);
      setSavedRule(null);
      setTranslating(false);
      setSaving(false);
    }
  }, [open]);

  if (!open) return null;

  const handleGenerate = async () => {
    setTranslating(true);
    setError(null);
    setErrorDetail(null);
    try {
      const result = await RulesAPI.translateFromNaturalLanguage({
        description: description.trim(),
      });
      setTranslation(result);
      setStep('review');
    } catch (err: unknown) {
      // The translator returns 422 with structured detail; surface it cleanly.
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = e?.response?.data?.detail;
      if (detail) setErrorDetail(detail);
      setError(getErrorMessage(err));
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    if (!translation) return;
    setSaving(true);
    setError(null);
    try {
      const result = await RulesAPI.saveWithRender({
        rule: translation.rule,
        plain_english_render: translation.plain_english_render,
      });
      setSavedRule({ id: result.id, name: result.name });
      setStep('saved');
      onSaved?.(result.id, result.name);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {step === 'describe' && 'Describe the new rule'}
              {step === 'review' && 'Review the generated rule'}
              {step === 'saved' && 'Rule saved'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {step === 'describe' && (
            <DescribeStep
              description={description}
              setDescription={setDescription}
              onGenerate={handleGenerate}
              translating={translating}
              error={error}
              errorDetail={errorDetail}
            />
          )}
          {step === 'review' && translation && (
            <ReviewStep
              translation={translation}
              description={description}
              showJson={showJson}
              setShowJson={setShowJson}
              error={error}
            />
          )}
          {step === 'saved' && savedRule && (
            <SavedStep ruleName={savedRule.name} onClose={onClose} />
          )}
        </div>

        {/* Footer */}
        {step !== 'saved' && (
          <div className="flex items-center justify-end gap-2 border-t bg-slate-50 px-6 py-3">
            {step === 'describe' && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={description.trim().length < 10 || translating}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                  {translating ? 'Generating…' : 'Generate rule'}
                </button>
              </>
            )}
            {step === 'review' && (
              <>
                <button
                  type="button"
                  onClick={() => setStep('describe')}
                  className="rounded-md px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-200"
                >
                  Edit description
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {saving ? 'Saving…' : 'Save rule'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

const DescribeStep: React.FC<{
  description: string;
  setDescription: (_v: string) => void;
  onGenerate: () => void;
  translating: boolean;
  error: string | null;
  errorDetail: unknown;
}> = ({ description, setDescription, error, errorDetail }) => (
  <div className="space-y-3">
    <label className="block text-sm font-medium text-slate-700">
      Describe the rule in plain English
    </label>
    <p className="text-xs text-slate-500">
      Example: "When the project has Cat 3 water and any porous floor material
      is being removed, add an antimicrobial floor seal (PNT S+++)."
    </p>
    <textarea
      value={description}
      onChange={(e) => setDescription(e.target.value)}
      rows={8}
      placeholder="Describe the rule here…"
      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    />
    {error && (
      <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">{error}</div>
            {errorDetail != null && (
              <pre className="mt-2 max-h-48 overflow-auto rounded bg-red-100 p-2 text-xs whitespace-pre-wrap">
                {typeof errorDetail === 'string'
                  ? errorDetail
                  : JSON.stringify(errorDetail, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
);

const ReviewStep: React.FC<{
  translation: TranslateRuleResponse;
  description: string;
  showJson: boolean;
  setShowJson: (_v: boolean) => void;
  error: string | null;
}> = ({ translation, description, showJson, setShowJson, error }) => {
  const conf = translation.confidence;
  const confColor =
    conf >= 0.8
      ? 'bg-green-50 border-green-300 text-green-800'
      : conf >= 0.6
      ? 'bg-amber-50 border-amber-300 text-amber-800'
      : 'bg-red-50 border-red-300 text-red-800';
  const confLabel =
    conf >= 0.8 ? 'High confidence' : conf >= 0.6 ? 'Moderate confidence' : 'Low confidence';

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
          Plain English
        </div>
        <p className="text-base text-slate-900 leading-relaxed">
          {translation.plain_english_render}
        </p>
      </div>

      <div className={cn('rounded-md border p-3 text-sm', confColor)}>
        <div className="flex items-center justify-between">
          <span className="font-medium">{confLabel}</span>
          <span className="font-mono text-xs">{(conf * 100).toFixed(0)}%</span>
        </div>
      </div>

      {translation.ambiguities.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <div className="font-medium text-amber-900 mb-1.5">Assumptions to confirm:</div>
          <ul className="list-disc pl-5 space-y-1 text-amber-800">
            {translation.ambiguities.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      {translation.warnings.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm">
          <div className="font-medium text-red-900 mb-1.5">Warnings:</div>
          <ul className="list-disc pl-5 space-y-1 text-red-800">
            {translation.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-md border bg-slate-50">
        <button
          type="button"
          onClick={() => setShowJson(!showJson)}
          className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
        >
          {showJson ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          Show underlying JSON
        </button>
        {showJson && (
          <pre className="overflow-auto border-t bg-white p-3 text-xs max-h-72">
            {JSON.stringify(translation.rule, null, 2)}
          </pre>
        )}
      </div>

      <details className="text-xs text-slate-500">
        <summary className="cursor-pointer">Original description</summary>
        <p className="mt-1 whitespace-pre-wrap">{description}</p>
      </details>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}
    </div>
  );
};

const SavedStep: React.FC<{ ruleName: string; onClose: () => void }> = ({
  ruleName,
  onClose,
}) => (
  <div className="py-6 text-center">
    <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
    <h3 className="mt-3 text-lg font-semibold">Rule saved</h3>
    <p className="mt-1 text-sm text-slate-600">
      <span className="font-mono">{ruleName}</span> has been added to the rule
      library with status <span className="font-mono">draft</span>. Promote to
      active when ready.
    </p>
    <button
      type="button"
      onClick={onClose}
      className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
    >
      Done
    </button>
  </div>
);

export default RuleAuthoringModal;
